<template>
  <div>
    <b-form-group
      label-cols="2"
      label="IOS"
      label-class="pt-0"
      :label-for="$id('bannerIOS')"
      description="Should the banner be on the iOS platform?">
      <b-form-checkbox
        :id="$id('bannerIOS')"
        v-model="banner.ios"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Android"
      label-class="pt-0"
      :label-for="$id('bannerAndroid')"
      description="Should the banner be on the Android platform?">
      <b-form-checkbox
        :id="$id('bannerAndroid')"
        v-model="banner.android"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Lite"
      label-class="pt-0"
      :label-for="$id('bannerLite')"
      description="Should the banner be on the lite version of the app?">
      <b-form-checkbox
        :id="$id('bannerLite')"
        v-model="banner.lite"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Paid"
      label-class="pt-0"
      :label-for="$id('bannerPaid')"
      description="Should the banner be on the paid version of the app?">
      <b-form-checkbox
        :id="$id('bannerPaid')"
        v-model="banner.paid"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Phone"
      label-class="pt-0"
      :label-for="$id('bannerPhone')"
      description="Should the banner show on phones?">
      <b-form-checkbox
        :id="$id('bannerPhone')"
        v-model="banner.phone"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Tablet"
      label-class="pt-0"
      :label-for="$id('bannerTablet')"
      description="Should the banner show on tablets?">
      <b-form-checkbox
        :id="$id('bannerTablet')"
        v-model="banner.tablet"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Screen"
      label-class="pt-0"
      :label-for="$id('bannerScreen')"
      description="The controllers to display this banner on">
      <b-form-input
        :id="$id('bannerScreen')"
        v-model="banner.screen"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Type"
      label-class="pt-0"
      :label-for="$id('bannerType')"
      description="The banner type">
      <b-form-input
        :id="$id('bannerType')"
        v-model="banner.type"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Launch Externally?"
      label-class="pt-0"
      :label-for="$id('bannerLaunchExternally')"
      description="Should the item launch outside of the app?">
      <!-- Options 0,1,2 for webviewtype have been deprecated, now when launchExternally is checked will set webviewtype to launchExternally(3) and unchecked will set to default webview(4) -->
      <b-form-checkbox
        :id="$id('bannerLaunchExternally')"
        v-model="banner.webviewtype"
        :disabled="!editable"
        :value="LAUNCH_EXTERNALLY_WEBVIEWTYPE"
        :unchecked-value="DEFAULT_WEBVIEWTYPE" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Title"
      label-class="pt-0"
      :label-for="$id('bannerTitle')"
      description="The title of the banner">
      <b-form-input
        :id="$id('bannerTitle')"
        v-model="banner.title"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Image (Phone, Tablet TV EPG)"
      label-class="pt-0"
      :label-for="$id('bannerImageUpload')"
      description="The banner image">
      <b-input-group>
        <b-form-input
          :id="$id('bannerImageUpload')"
          v-model="banner.image"
          :disabled="!editable"
          type="url"
          trim />
        <b-input-group-append>
          <b-btn
            variant="outline-primary"
            :disabled="!editable"
            @click.prevent="showUploadModal('image', 'Banner Image')">
            <i class="fas fa-plus" />
          </b-btn>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Image (Tablet Portrait)"
      label-class="pt-0"
      :label-for="$id('bannerImageTabletPortraitUpload')"
      description="The banner image for tablet, in portrait">
      <b-input-group>
        <b-form-input
          :id="$id('bannerImageTabletPortraitUpload')"
          v-model="banner.imageTabletPortrait"
          :disabled="!editable"
          type="url"
          trim />
        <b-input-group-append>
          <b-btn
            variant="outline-primary"
            :disabled="!editable"
            @click.prevent="
              showUploadModal(
                'imageTabletPortrait',
                'Banner Image (Tablet Portrait)'
              )
            ">
            <i class="fas fa-plus" />
          </b-btn>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Image (Tablet Landscape)"
      label-class="pt-0"
      :label-for="$id('bannerImageTabletLandscapeUpload')"
      description="The banner image for tablet, in landscape">
      <b-input-group>
        <b-form-input
          :id="$id('bannerImageTabletLandscapeUpload')"
          v-model="banner.imageTabletLandscape"
          :disabled="!editable"
          type="url"
          trim />
        <b-input-group-append>
          <b-btn
            variant="outline-primary"
            :disabled="!editable"
            @click.prevent="
              showUploadModal(
                'imageTabletLandscape',
                'Banner Image (Tablet Landscape)'
              )
            ">
            <i class="fas fa-plus" />
          </b-btn>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Closable"
      label-class="pt-0"
      :label-for="$id('bannerClosable')"
      description="Is the banner closable?">
      <b-form-checkbox
        :id="$id('bannerClosable')"
        v-model="banner.closable"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Link"
      label-class="pt-0"
      :label-for="$id('bannerLink')"
      description="The url for the banner to link to">
      <b-form-input
        :id="$id('bannerLink')"
        v-model="banner.link"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Background Color"
      label-class="pt-0"
      :label-for="$id('bannerBackgroundColor')"
      description="The background color of the banner container">
      <b-form-input
        :id="$id('bannerBackgroundColor')"
        v-model="banner.backgroundColor"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Start Date"
      label-class="pt-0"
      :label-for="$id('bannerStartDate')"
      description="The scheduled start date">
      <b-form-datepicker
        :id="$id('bannerStartDate')"
        v-model="banner.startDate"
        :reset-button="true"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="Start Time"
      label-class="pt-0"
      :label-for="$id('bannerStartTime')"
      description="The scheduled start time">
      <b-form-timepicker
        :id="$id('bannerStartTime')"
        v-model="banner.startTime"
        :reset-button="true"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="End Date"
      label-class="pt-0"
      :label-for="$id('bannerEndDate')"
      description="The scheduled end date">
      <b-form-datepicker
        :id="$id('bannerEndDate')"
        v-model="banner.endDate"
        :reset-button="true"
        :disabled="!editable" />
    </b-form-group>

    <b-form-group
      label-cols="2"
      label="End Time"
      label-class="pt-0"
      :label-for="$id('bannerEndTime')"
      description="The scheduled end time">
      <b-form-timepicker
        :id="$id('bannerendTime')"
        v-model="banner.endTime"
        :reset-button="true"
        :disabled="!editable" />
    </b-form-group>

    <b-modal
      id="assetUploadModal"
      ref="assetUploadModal"
      hide-header
      hide-footer
      size="lg">
      <asset-upload
        post-action="/api/client-content/v1/files/static/banner"
        :existing-files="[]"
        :process-image-cuts="true"
        :constraint="selectedConstraint"
        @asset-selected-response="assetSelectedResponse"
        @cancel="$refs.assetUploadModal.hide()" />
    </b-modal>
  </div>
</template>

<script>
import AssetUpload from "@/components/AssetUpload"
import imageconstraints from "@/services/imageconstraints"

export default {
  name: "Banner",
  components: {
    AssetUpload,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    editable: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      assetUploadTitle: null,
      LAUNCH_EXTERNALLY_WEBVIEWTYPE: "3",
      DEFAULT_WEBVIEWTYPE: "4",
      assetSelectedResponse: () => {},
      imageconstraints: imageconstraints,
      selectedConstraint: null,
    }
  },
  computed: {
    banner() {
      return this.value
    },
    CLOUDINARY_ACCOUNT() {
      return process.env.VUE_APP_CLOUDINARY_ACCOUNT
    },
  },
  watch: {
    banner: {
      deep: true,
      handler: function (val) {
        this.$emit("input", val)
      },
    },
  },
  methods: {
    showUploadModal(name, title) {
      this.assetUploadTitle = title
      this.getImageConstraint(name)
      this.assetSelectedResponse = (response) => {
        this.$refs.assetUploadModal.hide()

        if (response.url) {
          this.banner[`${name}PublicId`] = response.url
          this.banner[name] = response.url
        } else {
          console.warn("Ignoring invalid asset selection")
        }
      }
      this.$refs.assetUploadModal.show()
    },
    getImageConstraint(name) {
      switch (name) {
        case "image":
          this.selectedConstraint = imageconstraints.BannersTvEPG
          break
        case "imageTabletPortrait":
          this.selectedConstraint = imageconstraints.BannersTabletPortrait
          break
        case "imageTabletLandscape":
          this.selectedConstraint = imageconstraints.BannersTabletLandScape
          break
      }
    },
  },
}
</script>
